import { Session, User, UserGroupWithUserIds } from "@opengov/bnp-core";
import { IUserSelectOption, IGroupSelectOption, ISubjectSelectOption, ISelectGroup } from "../../types/subjectTypes";
import { IPublicationPrivilege, ISelectedSubject, ISelectedSubjectList, IStoryPrivilege, ISubjectPublicationClaim, ISubjectPublicationClaimsList, ISubjectStoryClaim, ISubjectStoryClaimsList } from '../../types';

type GetSortedOptionsParams = {
  session?: Session;
  users: User[];
  groups: UserGroupWithUserIds[];
  subjectStoryClaimsList?: ISubjectStoryClaimsList;
  subjectPublicationClaimsList?: ISubjectPublicationClaimsList;
};

export const getSortedOptions = ({ session, users, groups, subjectStoryClaimsList, subjectPublicationClaimsList }: GetSortedOptionsParams) => {
  const getSubjectIds = (claims: ISubjectStoryClaim[] | ISubjectPublicationClaim[] | undefined): Set<string> => {
    if (!claims) return new Set<string>();
    return new Set(claims.map((claim: ISubjectStoryClaim | ISubjectPublicationClaim) => claim.subjectId));
  };

  const existingIds = getSubjectIds(subjectStoryClaimsList || subjectPublicationClaimsList);

  const userOptions = users.reduce<IUserSelectOption[]>((acc, user: User) => {
    if (existingIds.has(user.uuid)) return acc; // skip if already exists
    return [
      ...acc,
      {
        id: user.uuid,
        label:
          user.uuid === session?.uuid
            ? user.full_name + " (You)"
            : user.full_name,
        subjectType: 'user',
        metadata: {
          email: user.email,
        },
      },
    ];
  }, []);
  const groupOptions: IGroupSelectOption[] = groups
    .filter((group: ISelectGroup) => !existingIds.has(group.id)) // ⛔ skip if already exists
    .map((group: ISelectGroup) => ({
      id: group.id,
      label: group.name,
      subjectType: 'group',
      metadata: {
        memberCount: group.memberCount,
      },
    }));
  return [...userOptions, ...groupOptions].sort((a, b) => a.label.localeCompare(b.label));
};

export const getUpdatedStoryPermissionsOnOptionsSelected = (subjectStoryClaimsList: ISubjectStoryClaimsList, selectedSubjectStoryClaimsList: ISubjectStoryClaimsList, options: ISubjectSelectOption[], storyId: string) => {
  const updatedSubjectStoryClaimsList = [...subjectStoryClaimsList];
  const updatedSelectedSubjectStoryClaimsList = [...selectedSubjectStoryClaimsList];
  options.forEach((option) => {
    const index = updatedSubjectStoryClaimsList.findIndex((claim) => claim.subjectId === option.id);
    if (index === -1) {
      if (option.subjectType === 'user') {
        updatedSubjectStoryClaimsList.push({ subjectId: option.id, subjectType: 'user', storyClaims: ["STORIES_VIEWER_PRIVILEGE"], storyId: storyId });
        updatedSelectedSubjectStoryClaimsList.push({ subjectId: option.id, subjectType: 'user', storyClaims: ["STORIES_VIEWER_PRIVILEGE"], storyId: storyId });
      } else {
        updatedSubjectStoryClaimsList.push({ subjectId: option.id, subjectType: 'group', storyClaims: ["STORIES_VIEWER_PRIVILEGE"], storyId: storyId });
        updatedSelectedSubjectStoryClaimsList.push({ subjectId: option.id, subjectType: 'group', storyClaims: ["STORIES_VIEWER_PRIVILEGE"], storyId: storyId });
      }
    }
  });
  return [updatedSubjectStoryClaimsList, updatedSelectedSubjectStoryClaimsList];
};

export const getUpdatedPublicationPermissionsOnOptionsSelected = (subjectPublicationClaimsList: ISubjectPublicationClaimsList, selectedSubjectPublicationClaimsList: ISubjectPublicationClaimsList, options: ISubjectSelectOption[], publicationId: string) => {
  const updatedSubjectPublicationClaimsList = [...subjectPublicationClaimsList];
  const updatedSelectedSubjectPublicationClaimsList = [...selectedSubjectPublicationClaimsList];
  options.forEach((option) => {
    const index = updatedSubjectPublicationClaimsList.findIndex((claim) => claim.subjectId === option.id);
    if (index === -1) {
      if (option.subjectType === 'user') {
        updatedSubjectPublicationClaimsList.push({ subjectId: option.id, subjectType: 'user', publicationClaim: "manage_shared_and_view_all", publicationId: publicationId, sectionIds: [] });
        updatedSelectedSubjectPublicationClaimsList.push({ subjectId: option.id, subjectType: 'user', publicationClaim: "manage_shared_and_view_all", publicationId: publicationId, sectionIds: [] });
      } else {
        updatedSubjectPublicationClaimsList.push({ subjectId: option.id, subjectType: 'group', publicationClaim: "manage_shared_and_view_all", publicationId: publicationId, sectionIds: [] });
        updatedSelectedSubjectPublicationClaimsList.push({ subjectId: option.id, subjectType: 'group', publicationClaim: "manage_shared_and_view_all", publicationId: publicationId, sectionIds: [] });
      }
    }
  });
  return [updatedSubjectPublicationClaimsList, updatedSelectedSubjectPublicationClaimsList];
};

export const getUpdatedBulkStoryPermissionsOnOptionsSelected = (selectedSubjectsList: ISelectedSubjectList, selectedSubjectStoryClaimsList: ISubjectStoryClaimsList, options: ISubjectSelectOption[], storyIds: string[]): [ISelectedSubjectList, ISubjectStoryClaimsList] => {
  const updatedSelectedSubjectStoryClaimsList = [...selectedSubjectStoryClaimsList];
  const updatedSelectedSubjectList = [...selectedSubjectsList];
  storyIds.forEach((storyId) => {
    options.forEach((option) => {
      if (option.subjectType === 'user') {
        updatedSelectedSubjectStoryClaimsList.push({ subjectId: option.id, subjectType: 'user', storyClaims: ["STORIES_VIEWER_PRIVILEGE"], storyId: storyId });
      } else {
        updatedSelectedSubjectStoryClaimsList.push({ subjectId: option.id, subjectType: 'group', storyClaims: ["STORIES_VIEWER_PRIVILEGE"], storyId: storyId });
      }
    });
  });
  options.forEach((option) => {
    const index = updatedSelectedSubjectList.findIndex((claim) => claim.subjectId === option.id);
    if (index === -1) {
      updatedSelectedSubjectList.push({ subjectId: option.id, subjectType: option.subjectType, storyClaims: ["STORIES_VIEWER_PRIVILEGE"] });
    }
  });
  return [updatedSelectedSubjectList, updatedSelectedSubjectStoryClaimsList];
};

export const updateStoryClaimsForSubject = (
  subjectId: string,
  privilege: IStoryPrivilege,
  checked: boolean,
  subjectStoryClaimsList: ISubjectStoryClaimsList,
  selectedSubjectStoryClaimsList: ISubjectStoryClaimsList
): {
  updatedClaims: ISubjectStoryClaimsList;
  updatedSelectedClaims: ISubjectStoryClaimsList;
} => {
  const updatedSelectedClaims = [...selectedSubjectStoryClaimsList];

  const updatedClaims = subjectStoryClaimsList.map((claim: ISubjectStoryClaim) => {
    if (claim.subjectId !== subjectId) return claim;

    const storyClaims = checked
      ? [...new Set([...claim.storyClaims, privilege])]
      : claim.storyClaims.filter((p) => p !== privilege);

    const updatedClaim = { ...claim, storyClaims };

    const index = updatedSelectedClaims.findIndex((c) => c.subjectId === subjectId);
    if (index === -1) {
      updatedSelectedClaims.push(updatedClaim);
    } else {
      updatedSelectedClaims[index] = updatedClaim;
    }

    return updatedClaim;
  });

  return {
    updatedClaims,
    updatedSelectedClaims,
  };
};

export const updatePublicationClaimsForSubject = (
  subjectId: string,
  privilege: IPublicationPrivilege,
  subjectPublicationClaimsList: ISubjectPublicationClaimsList,
  selectedSubjectPublicationClaimsList: ISubjectPublicationClaimsList
): {
  updatedClaims: ISubjectPublicationClaimsList;
  updatedSelectedClaims: ISubjectPublicationClaimsList;
} => {
  const updatedSelectedClaims = [...selectedSubjectPublicationClaimsList];

  const updatedClaims = subjectPublicationClaimsList.map((claim: ISubjectPublicationClaim) => {
    if (claim.subjectId !== subjectId) return claim;

    const updatedClaim = { ...claim, publicationClaim: privilege };

    const index = updatedSelectedClaims.findIndex((c: ISubjectPublicationClaim) => c.subjectId === subjectId);
    if (index === -1) {
      updatedSelectedClaims.push(updatedClaim);
    } else {
      updatedSelectedClaims[index] = updatedClaim;
    }

    return updatedClaim;
  });

  return {
    updatedClaims,
    updatedSelectedClaims,
  };
};

export const updatePublicationClaimsSectionIdsForSubject = (
  subjectId: string,
  sectionIds: string[],
  subjectPublicationClaimsList: ISubjectPublicationClaimsList,
  selectedSubjectPublicationClaimsList: ISubjectPublicationClaimsList
): {
  updatedClaims: ISubjectPublicationClaimsList;
  updatedSelectedClaims: ISubjectPublicationClaimsList;
} => {
  const updatedSelectedClaims = [...selectedSubjectPublicationClaimsList];

  const updatedClaims = subjectPublicationClaimsList.map((claim: ISubjectPublicationClaim) => {
    if (claim.subjectId !== subjectId) return claim;

    const updatedClaim = { ...claim, sectionIds: sectionIds };

    const index = updatedSelectedClaims.findIndex((c: ISubjectPublicationClaim) => c.subjectId === subjectId);
    if (index === -1) {
      updatedSelectedClaims.push(updatedClaim);
    } else {
      updatedSelectedClaims[index] = updatedClaim;
    }

    return updatedClaim;
  });

  return {
    updatedClaims,
    updatedSelectedClaims,
  };
};

export const updateBulkSubjectStoryClaims = (
  subjectId: string,
  privilege: IStoryPrivilege,
  checked: boolean,
  selectedSubjectsList: ISelectedSubjectList,
  selectedSubjectStoryClaimsList: ISubjectStoryClaimsList
): {
  updatedSubjectsList: ISelectedSubjectList;
  updatedSelectedClaims: ISubjectStoryClaimsList;
} => {
  const updatedSelectedClaims = selectedSubjectStoryClaimsList.map((claim: ISubjectStoryClaim) => {
    if (claim.subjectId !== subjectId) return claim;

    const storyClaims = checked
      ? [...new Set([...claim.storyClaims, privilege])]
      : claim.storyClaims.filter((p) => p !== privilege);

    return { ...claim, storyClaims };
  });

  const updatedSubjectsList = selectedSubjectsList.map((subject: ISelectedSubject) => {
    if (subject.subjectId !== subjectId) return subject;
    const storyClaims = checked
      ? [...new Set([...subject.storyClaims, privilege])]
      : subject.storyClaims.filter((p) => p !== privilege);
    return { ...subject, storyClaims };
  });

  return {
    updatedSubjectsList,
    updatedSelectedClaims,
  };
}

export const getSubjectStoryClaimsToDelete = (
  subjectId: string,
  subjectStoryClaimsList: ISubjectStoryClaimsList,
  selectedSubjectStoryClaimsList: ISubjectStoryClaimsList,
  subjectIdsToDelete: string[]
): {
  updatedClaims: ISubjectStoryClaimsList;
  updatedSelectedClaims: ISubjectStoryClaimsList;
  updatedSubjectIdsToDelete: string[];
} => {
  const updatedSubjectIdsToDelete = [...subjectIdsToDelete];

  const isMatchingSubject = (claim: { subjectId: string }) =>
    claim.subjectId === subjectId;

  const updatedSelectedClaims = selectedSubjectStoryClaimsList.filter(
    (claim) => !isMatchingSubject(claim)
  );

  const updatedClaims = subjectStoryClaimsList.filter(
    (claim) => !isMatchingSubject(claim)
  );

  updatedSubjectIdsToDelete.push(subjectId);

  return {
    updatedClaims,
    updatedSelectedClaims,
    updatedSubjectIdsToDelete
  };
};

export const getSubjectPublicationClaimsToDelete = (
  subjectId: string,
  subjectPublicationClaimsList: ISubjectPublicationClaimsList,
  selectedSubjectPublicationClaimsList: ISubjectPublicationClaimsList,
  subjectIdsToDelete: string[]
): {
  updatedClaims: ISubjectPublicationClaimsList;
  updatedSelectedClaims: ISubjectPublicationClaimsList;
  updatedSubjectIdsToDelete: string[];
} => {
  const updatedSubjectIdsToDelete = [...subjectIdsToDelete];

  const isMatchingSubject = (claim: { subjectId: string }) =>
    claim.subjectId === subjectId;

  const updatedSelectedClaims = selectedSubjectPublicationClaimsList.filter(
    (claim) => !isMatchingSubject(claim)
  );

  const updatedClaims = subjectPublicationClaimsList.filter(
    (claim) => !isMatchingSubject(claim)
  );

  updatedSubjectIdsToDelete.push(subjectId);

  return {
    updatedClaims,
    updatedSelectedClaims,
    updatedSubjectIdsToDelete
  };
};

export const removeSelectedSubjectStoryClaims = (
  subjectId: string,
  selectedSubjectsList: ISelectedSubjectList,
  selectedSubjectStoryClaimsList: ISubjectStoryClaimsList
): {
  updatedSelectedSubjectsList: ISelectedSubjectList;
  updatedSelectedSubjectStoryClaimsList: ISubjectStoryClaimsList;
} => {
  const updatedSelectedSubjectsList = selectedSubjectsList.filter(
    (subject: ISelectedSubject) => subject.subjectId !== subjectId
  );

  const updatedSelectedSubjectStoryClaimsList = selectedSubjectStoryClaimsList.filter(
    (claim: ISubjectStoryClaim) => claim.subjectId !== subjectId
  );

  return {
    updatedSelectedSubjectsList,
    updatedSelectedSubjectStoryClaimsList,
  };
}
